import React, { useState, useEffect } from 'react';
import { saveField, loadUserData } from '../supabase';

// ─────────────────────────────────────────────────────────────────────────
// HomeScreenBanner — a small, dismissible nudge on Today asking the user
// to add the app to their home screen.
//
// Visibility rules:
//   • HIDDEN if the app is already running standalone (i.e. the user did
//     install to home screen and is launching from there).
//   • HIDDEN on desktop browsers (no point — desktops don't pin PWAs the
//     same way). Detected by absence of touch capability + wide viewport.
//   • HIDDEN if the user previously dismissed it (saved to Supabase).
//
// Once dismissed via the X, the banner never shows again on any device for
// that user — we save 'home_screen_dismissed: true' to Supabase.
// ─────────────────────────────────────────────────────────────────────────

const G = '#3A6A2A';

function isStandalone() {
  // iOS Safari uses navigator.standalone; everywhere else uses display-mode.
  if (typeof navigator !== 'undefined' && navigator.standalone === true) return true;
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(display-mode: standalone)').matches;
  }
  return false;
}

function isMobile() {
  if (typeof window === 'undefined') return false;
  // Touch + narrow viewport is a reasonable mobile signal.
  // We don't show this banner on desktop browsers.
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isNarrow = window.innerWidth <= 768;
  return hasTouch && isNarrow;
}

export default function HomeScreenBanner({ userId }) {
  const [show, setShow] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function decideVisibility() {
      // Quick exits — never show in these cases.
      if (isStandalone()) return; // already installed
      if (!isMobile()) return; // desktop — no point

      // Detect iOS for instruction copy.
      const ua = navigator.userAgent || '';
      const ios = /iPhone|iPad|iPod/i.test(ua);
      if (!cancelled) setIsIOS(ios);

      // Check Supabase for prior dismissal. localStorage is a fast fallback.
      const localDismissed = localStorage.getItem('bhn_home_screen_dismissed');
      if (localDismissed === 'true') return;

      try {
        if (userId) {
          const data = await loadUserData(userId);
          if (data && data.home_screen_dismissed === true) {
            localStorage.setItem('bhn_home_screen_dismissed', 'true');
            return;
          }
        }
      } catch (e) {
        // Supabase unreachable — fall through and show banner if no local flag.
      }

      if (!cancelled) setShow(true);
    }

    decideVisibility();
    return () => { cancelled = true; };
  }, [userId]);

  async function dismiss() {
    setShow(false);
    localStorage.setItem('bhn_home_screen_dismissed', 'true');
    try {
      if (userId) await saveField(userId, 'home_screen_dismissed', true);
    } catch (e) {
      // localStorage will keep it dismissed locally even if Supabase write fails.
    }
  }

  if (!show) return null;

  return (
    <>
      {/* The banner itself — solid green for high visibility */}
      <div
        style={{
          background: G,
          border: 'none',
          borderRadius: 14,
          padding: '16px 18px',
          marginBottom: 18,
          display: 'flex', alignItems: 'center', gap: 14,
          boxShadow: '0 4px 12px rgba(58, 106, 42, 0.15)',
        }}
      >
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: 'rgba(255, 255, 255, 0.18)', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {/* Phone-with-plus icon, white on green */}
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="6" y="2" width="12" height="20" rx="2" stroke="#FFFFFF" strokeWidth="1.8" />
            <line x1="12" y1="9" x2="12" y2="15" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" />
            <line x1="9" y1="12" x2="15" y2="12" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#FFFFFF', marginBottom: 3, letterSpacing: '-.2px' }}>
            Add to home screen
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.4 }}>
            One tap to return. Full screen, like a real app.
          </div>
        </div>
        <button
          onClick={() => setShowInstructions(true)}
          style={{
            background: '#FFFFFF', color: G, border: 'none',
            borderRadius: 8, padding: '9px 16px',
            fontSize: 12, fontWeight: 800, cursor: 'pointer',
            fontFamily: 'inherit', flexShrink: 0,
            letterSpacing: '.02em',
          }}
        >
          Show me
        </button>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          style={{
            background: 'transparent', border: 'none',
            padding: 4, cursor: 'pointer',
            color: 'rgba(255, 255, 255, 0.7)', flexShrink: 0,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Instructions modal */}
      {showInstructions && (
        <div
          onClick={() => setShowInstructions(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#FFFFFF', borderRadius: 20,
              padding: '28px 24px',
              paddingBottom: 'max(28px, env(safe-area-inset-bottom))',
              width: '100%', maxWidth: 480,
              maxHeight: 'calc(100dvh - 80px)',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#111', letterSpacing: '-.5px' }}>
                Add to home screen
              </div>
              <button
                onClick={() => setShowInstructions(false)}
                aria-label="Close"
                style={{
                  background: 'transparent', border: 'none', padding: 4,
                  cursor: 'pointer', color: '#A09888',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div style={{ fontSize: 14, color: '#666', lineHeight: 1.7, marginBottom: 22 }}>
              {isIOS
                ? 'On iPhone, this opens Be Here Now full-screen — no browser bar, just like a real app.'
                : 'This opens Be Here Now full-screen — no browser bar, just like a real app.'}
            </div>

            <div style={{ background: '#FAFAF8', borderRadius: 12, padding: '18px 20px', border: '1px solid #E8E4DF' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#A09888', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 12 }}>
                {isIOS ? 'On iPhone' : 'On Android'}
              </div>
              {(isIOS
                ? [
                    'Tap the Share button at the bottom of your screen',
                    'Scroll down and tap "Add to Home Screen"',
                    'Tap Add — done.',
                  ]
                : [
                    'Tap the three dots menu in the top right of your browser',
                    'Tap "Add to Home Screen"',
                    'Tap Add — done.',
                  ]
              ).map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: G, color: 'white', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ fontSize: 14, color: '#333', lineHeight: 1.55, paddingTop: 1 }}>{s}</div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowInstructions(false)}
              style={{
                width: '100%', marginTop: 18,
                background: G, color: 'white', border: 'none',
                borderRadius: 10, padding: '14px',
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit', letterSpacing: '.02em',
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
